/**
 * ReserveLibrarySheet - 予約中にするときに「どの図書館で予約したか」を選ぶシート。
 *
 * 候補は蔵書検索で取得済みの所蔵館。BookCard 側で組み立てた
 * 「早く借りられる順」のエントリをそのまま受け取り、同じ配色のチップで並べる。
 * 所蔵していない館でも取り寄せで予約できるため、地域の全館から館名で検索して選べる。
 */

import { Check, Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useLibrarySearchStore } from '@/store/useLibrarySearchStore'

import { CHIP_TONE_CLASS, pickChipTone, type RankedEntry } from '../lib/holding-chip'
import { buildLibraryEntries, filterLibrariesByName } from '../lib/library-mapping'
import { deriveAvailability } from '../lib/library-stats'
import { StatsBadge } from './HoldingChip'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  bookTitle: string
  /** 所蔵館 (BookCard と同じ「早く借りられる順」) */
  entries: RankedEntry[]
  /** 所蔵館の取得中 (ISBN 二次検索が走っている間) */
  loading: boolean
  /** すでに予約中か (現在の選択を示すかどうかの判定に使う) */
  reserved: boolean
  /** 現在の予約先 (館未指定なら null) */
  currentLibraryId: number | null
  /** 現在の予約先の館名スナップショット */
  currentLibraryName: string | null
  /** 選択された館。「指定しない」を選んだ場合は null */
  onSelect: (library: { id: number; name: string } | null) => void
}

export const ReserveLibrarySheet = ({
  open,
  onOpenChange,
  bookTitle,
  entries,
  loading,
  reserved,
  currentLibraryId,
  currentLibraryName,
  onSelect,
}: Props) => {
  const mapping = useLibrarySearchStore((s) => s.mapping)
  const [keyword, setKeyword] = useState('')

  const holdingIds = useMemo(() => new Set(entries.map((e) => e.libraryId)), [entries])
  const otherLibraries = useMemo(
    () => buildLibraryEntries(mapping).filter((e) => !holdingIds.has(e.id)),
    [mapping, holdingIds],
  )
  const searchResults = useMemo(
    () => (keyword.trim() ? filterLibrariesByName(otherLibraries, keyword) : []),
    [otherLibraries, keyword],
  )
  // 取り寄せで予約中の館は所蔵館一覧に出ないため、別枠で現在の選択として示す。
  const currentOther =
    reserved && !loading && currentLibraryId != null && !holdingIds.has(currentLibraryId)
      ? {
          id: currentLibraryId,
          name: currentLibraryName ?? mapping[String(currentLibraryId)] ?? `library:${currentLibraryId}`,
        }
      : null

  const handleOpenChange = (next: boolean) => {
    if (!next) setKeyword('')
    onOpenChange(next)
  }

  const handleSelect = (library: { id: number; name: string } | null) => {
    onSelect(library)
    handleOpenChange(false)
  }

  return (
    <BottomSheet title="予約した図書館を選ぶ" open={open} onOpenChange={handleOpenChange} snapPoints={[0.7]}>
      <div className="flex flex-col gap-3 px-4 pb-6">
        <div className="wrap-break-word text-xs text-muted-foreground">{bookTitle}</div>

        {loading && entries.length === 0 && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            所蔵館を取得中...
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="py-6 text-sm text-muted-foreground">
            所蔵館の情報がありません。取り寄せる図書館を下で検索するか、図書館を指定せずに予約中にできます。
          </div>
        )}

        <div className="flex flex-col gap-2">
          {entries.map((entry) => {
            const availability = deriveAvailability(entry.derived)
            const tone = pickChipTone(availability, entry.derived)
            const isCurrent = reserved && entry.libraryId === currentLibraryId
            return (
              <button
                key={entry.libraryId}
                type="button"
                onClick={() => handleSelect({ id: entry.libraryId, name: entry.name })}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left text-sm text-white transition-colors',
                  CHIP_TONE_CLASS[tone],
                  'hover:brightness-125 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isCurrent && 'ring-2 ring-emerald-400',
                )}
              >
                <span className="min-w-0 flex-1 wrap-break-word">{entry.name}</span>
                <span className="flex shrink-0 items-center gap-2">
                  <StatsBadge
                    derived={entry.derived}
                    isFetching={entry.isFetching}
                    availability={availability}
                    tone={tone}
                  />
                  {isCurrent && <Check className="size-4 text-emerald-400" />}
                </span>
              </button>
            )
          })}
        </div>

        <div className="flex flex-col gap-2 border-t border-border/40 pt-3">
          <div className="text-xs text-muted-foreground">所蔵していない図書館で予約 (取り寄せ)</div>
          {currentOther && (
            <PlainOptionButton label={currentOther.name} checked onClick={() => handleSelect(currentOther)} />
          )}
          <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="館名で検索" />
          {keyword.trim() && searchResults.length === 0 && (
            <div className="py-2 text-sm text-muted-foreground">該当する図書館がありません</div>
          )}
          {searchResults.map((library) => (
            <PlainOptionButton
              key={library.id}
              label={library.name}
              checked={reserved && library.id === currentLibraryId}
              onClick={() => handleSelect(library)}
            />
          ))}
        </div>

        <PlainOptionButton
          label="図書館を指定しない"
          checked={reserved && currentLibraryId == null}
          onClick={() => handleSelect(null)}
        />
      </div>
    </BottomSheet>
  )
}

const PlainOptionButton = ({ label, checked, onClick }: { label: string; checked: boolean; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex w-full items-center justify-between gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors',
      'hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      checked && 'text-foreground ring-2 ring-emerald-400',
    )}
  >
    <span className="min-w-0 flex-1 wrap-break-word">{label}</span>
    {checked && <Check className="size-4 shrink-0 text-emerald-400" />}
  </button>
)
